/**
 * Fueling-tier presentation + classification, shared between the server (Prisma
 * persistence + planner routes) and the customer SPA (`customer-app/src/lib/`).
 *
 * SOURCE OF TRUTH FOR BREAKPOINTS: `FuelPlanPolicy.durationTiers`, the DB-backed
 * fuel policy resolved at runtime by `resolveActiveFuelPlanPolicy(prisma)`. The
 * ONLY classifier exposed here is `classifyFuelTierFromPolicy` — every
 * production caller has a policy in scope (server resolves live; SPA reads
 * `plan.policySnapshot` for plan views, the planner range response for the
 * planner, `/api/fuel-plans/v3/policy/active` for activities). PR-G #4
 * removed the bundled-breakpoint fallback so silent policy drift can't
 * sneak back in via a code path that "didn't have a policy in scope".
 *
 * The tier id namespace `t1..t6` is just a 1-based index into the policy's
 * `durationTiers` array (index 0 -> t1, index 1 -> t2, ...).
 *
 * Tier COLOURS (`chipColor`/`dark`) are NOT in the policy schema — they are a
 * legitimately presentational UI palette keyed by tier id and stay here so the
 * SPA + EJS share one palette.
 *
 * The `label`/`minDurationMin`/`maxDurationMin`/`carbsPerHour` fields below
 * are kept as a LAST-RESORT first-paint fallback for SPA surfaces whose
 * policy fetch hasn't resolved yet (one render tick). Production reads
 * always come from the policy — these numbers are reference-only. Update
 * them only to match the bundled-file default policy.
 *
 * Breakpoints are duration-only. Intensity colors a stripe on the calendar chip
 * but doesn't move the tier — the engine treats intensity separately when
 * choosing carb targets. Sport and `eligibility` don't shift the tier either
 * (a 90-min strength session and a 90-min run both land in t3).
 *
 * Vocabulary note: the planner/DB intensity enum uses `'mod'` (`INTENSITY_KEYS`
 * below + the planner Zod schema), while the fuel policy schema uses
 * `'moderate'` (`IntensityLevelPolicy`). This divergence is deliberate and kept
 * to avoid a DB enum migration; the helper `intensityKeyToPolicyLevel` /
 * `policyLevelToIntensityKey` bridge the two when a mapping is needed.
 */

import type { IntensityLevelPolicy } from "../modules/fuelPlans/policy/schema.js";

export type FuelTierId = "t1" | "t2" | "t3" | "t4" | "t5" | "t6";

export type FuelTier = {
  id: FuelTierId;
  label: string;
  /** Inclusive lower bound in minutes. */
  minDurationMin: number;
  /** Exclusive upper bound in minutes. `Infinity` for the top tier. */
  maxDurationMin: number;
  /** Engine's nominal carb target band, in grams per hour. UI only. */
  carbsPerHour: { low: number; high: number };
  /** Hex chip background — kept here so the SPA + EJS share one palette. */
  chipColor: string;
  /** When true, chip ink should switch to white for contrast. */
  dark: boolean;
};

/**
 * The six tiers, in ascending duration order. The `chipColor`/`dark` fields
 * are the authoritative UI palette. The `minDurationMin`/`maxDurationMin`/
 * `carbsPerHour` fields are a DISPLAY-ONLY FALLBACK — the real breakpoints
 * live in `FuelPlanPolicy.durationTiers`. Server code MUST classify via
 * `classifyFuelTierFromPolicy`; these numbers only back `classifyFuelTier`
 * for code paths with no policy in scope.
 *
 * These bounds/bands/labels mirror the bundled 6-tier default policy (spec C1):
 * duration `maxMinutesInclusive` = 49/74/89/119/179/null → display upper bounds
 * 50/75/90/120/180/∞.
 */
export const FUEL_TIERS: readonly FuelTier[] = [
  {
    id: "t1",
    label: "Minimal fuel",
    minDurationMin: 0,
    maxDurationMin: 50,
    carbsPerHour: { low: 0, high: 15 },
    chipColor: "#FCEFAE",
    dark: false
  },
  {
    id: "t2",
    label: "Light fuel",
    minDurationMin: 50,
    maxDurationMin: 75,
    carbsPerHour: { low: 15, high: 30 },
    chipColor: "#E4EFA1",
    dark: false
  },
  {
    id: "t3",
    label: "Steady fuel",
    minDurationMin: 75,
    maxDurationMin: 90,
    carbsPerHour: { low: 30, high: 45 },
    chipColor: "#C5DC8A",
    dark: false
  },
  {
    id: "t4",
    label: "Sustained fuel",
    minDurationMin: 90,
    maxDurationMin: 120,
    carbsPerHour: { low: 45, high: 60 },
    chipColor: "#A5CE72",
    dark: false
  },
  {
    id: "t5",
    label: "Structured endurance fuel",
    minDurationMin: 120,
    maxDurationMin: 180,
    carbsPerHour: { low: 50, high: 70 },
    chipColor: "#88B655",
    dark: false
  },
  {
    id: "t6",
    label: "Long-day fuel stack",
    minDurationMin: 180,
    maxDurationMin: Number.POSITIVE_INFINITY,
    carbsPerHour: { low: 60, high: 80 },
    chipColor: "#5A8E29",
    dark: true
  }
] as const;

/** The ordered tier-id namespace. `tierIdForIndex(i)` -> `t{i+1}`, capped at t6. */
const TIER_IDS: readonly FuelTierId[] = ["t1", "t2", "t3", "t4", "t5", "t6"];

/**
 * Map a 0-based `durationTiers` array index to a `FuelTierId`. Caps at `t6` if
 * the policy somehow defines more than six tiers, and clamps a negative index
 * to `t1`.
 */
function tierIdForIndex(index: number): FuelTierId {
  if (index <= 0) return "t1";
  return TIER_IDS[Math.min(index, TIER_IDS.length - 1)];
}

/**
 * POLICY-DRIVEN CLASSIFIER — the authoritative path. Walks `policy.durationTiers`
 * using the same inclusive-upper-bound semantics as `durationTier()` in
 * `recommendation/durationTier.ts` (a tier matches when
 * `maxMinutesInclusive == null || minutes <= maxMinutesInclusive`) and returns
 * the 1-based tier id for the matching index.
 *
 * Handles a policy with fewer than six tiers gracefully (the last tier absorbs
 * everything past its bound) and caps at `t6` for a policy with more than six.
 * Non-finite / negative input falls back to `t1`.
 */
/**
 * Narrow shape this classifier actually depends on — just the duration-tier
 * boundaries. Exported so SPA call sites (which receive a `FuelPolicySnapshot`
 * derived from the policy wire shape rather than a full `FuelPlanPolicy`) can
 * pass through without an `as unknown as FuelPlanPolicy` cast.
 */
export type FuelTierClassifierPolicy = {
  durationTiers: ReadonlyArray<{ maxMinutesInclusive: number | null }>;
};

export function classifyFuelTierFromPolicy(
  durationMin: number,
  policy: FuelTierClassifierPolicy
): FuelTierId {
  if (!Number.isFinite(durationMin) || durationMin < 0) return "t1";
  const tiers = policy.durationTiers;
  for (let i = 0; i < tiers.length; i++) {
    const t = tiers[i];
    if (t.maxMinutesInclusive == null || durationMin <= t.maxMinutesInclusive) {
      return tierIdForIndex(i);
    }
  }
  // Past the last finite bound — clamp to the last tier (mirrors durationTier()).
  return tierIdForIndex(tiers.length - 1);
}

// PR-G #4: the `classifyFuelTier` fallback (hardcoded breakpoints, no policy
// in scope) was removed once every production caller had a `FuelPlanPolicy`
// in hand — server creates/updates go through `classifyFuelTierFromPolicy`,
// SPA planner edits use the policy shipped on the range response, plan view
// surfaces read `plan.policySnapshot`. Keep the classifier surface narrow:
// one policy-driven function, no silent-drift escape hatch.

export function getFuelTier(id: FuelTierId): FuelTier {
  const t = FUEL_TIERS.find((x) => x.id === id);
  if (!t) throw new Error(`Unknown fuel tier id: ${id}`);
  return t;
}

/** Allowed values for `PlannedWorkout.intensity`. Shared with the planner Zod schema. */
export const INTENSITY_KEYS = ["low", "mod", "high"] as const;
export type IntensityKey = (typeof INTENSITY_KEYS)[number];

/**
 * Bridge the planner/DB intensity vocabulary (`'mod'`) and the fuel-policy
 * vocabulary (`'moderate'`). Only `mod`/`moderate` differ; `low` and `high` are
 * identical in both. See the file header for why the divergence is kept.
 */
export function intensityKeyToPolicyLevel(key: IntensityKey): IntensityLevelPolicy {
  return key === "mod" ? "moderate" : key;
}

export function policyLevelToIntensityKey(level: IntensityLevelPolicy): IntensityKey {
  return level === "moderate" ? "mod" : level;
}

/** Allowed values for `PlannedWorkout.sport`. */
export const SPORT_KEYS = ["run", "ride", "swim", "strength", "hyrox", "other"] as const;
export type SportKey = (typeof SPORT_KEYS)[number];

/** Allowed values for `PlannedWorkout.eligibility`. */
export const ELIGIBILITY_KEYS = ["auto", "hydration", "excluded"] as const;
export type EligibilityKey = (typeof ELIGIBILITY_KEYS)[number];

/**
 * Allowed values for `PlannedWorkout.source`. `manual_assumed` is the
 * manual-cadence onboarding synthesis (user-owned); provider ids
 * (`trainingpeaks`, `vdot`, `endorphins`) are server-created only.
 * Ownership predicates live in `src/lib/workoutSources.ts`.
 */
export const SOURCE_KEYS = [
  "manual",
  "manual_assumed",
  "trainingpeaks",
  "vdot",
  "endorphins"
] as const;
export type SourceKey = (typeof SOURCE_KEYS)[number];
